import { useMemo } from 'react';

import type * as api from '@gitbook/api-client';

import { getSiteSpacesFromStructure } from '../../../state';
import { flattenSpacesInOrgAllContent } from '../../Organization';

export interface UseAvailableSiteSpacesProps {
    siteStructure: api.SiteStructure;
    orgAllContent: api.OrganizationAllContent;
}

/**
 * Returns the available spaces for a site based on the site structure and organization content.
 */
export function getAvailableSiteSpaces(
    siteStructure: api.SiteStructure,
    orgAllContent: api.OrganizationAllContent
): api.Space[] {
    const structureSpaceIds = new Set(
        getSiteSpacesFromStructure(siteStructure).map((siteSpace) => siteSpace.space.id)
    );

    return flattenSpacesInOrgAllContent(orgAllContent, ({ space }) =>
        structureSpaceIds.has(space.id) ? null : space
    );
}

/**
 * Returns the available spaces for a site based on the site structure and organization content.
 * It filters the spaces to only include those that are not part of the site structure.
 */
export function useAvailableSiteSpaces(props: UseAvailableSiteSpacesProps) {
    const { siteStructure, orgAllContent } = props;
    return useMemo(() => {
        return getAvailableSiteSpaces(siteStructure, orgAllContent);
    }, [siteStructure, orgAllContent]);
}
